"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  ErrorNotice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, LABEL } from "@/components/tool-kit/styles";
import {
  HillWorking,
  MatrixInputs,
  MatrixView,
} from "@/components/tools/cipher-machines/hill-working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type HillConvention,
  type HillResult,
  type HillSize,
  hillDecrypt,
  hillEncrypt,
  invertibility,
  type Matrix,
  matrixFromEntries,
  matrixFromKeyword,
} from "@/lib/tool-kit/ciphers/hill";

type KeySource = "numbers" | "keyword";

const FILLERS = ["X", "Z", "Q"] as const;
type Filler = (typeof FILLERS)[number];

/** Wikipedia's two examples: HELP → HIAT, and ACT → POH with GYBNQKURP. */
const DEFAULT_ENTRIES: Readonly<Record<HillSize, readonly string[]>> = {
  2: ["3", "3", "2", "5"],
  3: ["6", "24", "1", "13", "16", "10", "20", "17", "15"],
};

/** English strings. A translated content file overrides these through `ui`. */
export const HILL_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Cipher text",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  sample: "HELP",
  size: "Key matrix size",
  size2: "2 × 2 (pairs of letters)",
  size3: "3 × 3 (triples of letters)",
  keySource: "Enter the key as",
  keyNumbers: "Numbers",
  keyKeyword: "A keyword",
  matrix: "Key matrix K — whole numbers, taken mod 26",
  entryLabel: "Row {row}, column {column}",
  badEntry: "Every entry must be a whole number.",
  keyword: "Keyword",
  keyword2: "HILL",
  keyword3: "GYBNQKURP",
  keywordHint:
    "Its first {count} letters fill the matrix row by row, with A = 0, B = 1 … Z = 25.",
  keywordShort:
    "one: The keyword needs {count} letter.|other: The keyword needs {count} letters.",
  keyFromKeyword: "The key matrix",
  convention: "Write each block as",
  conventionColumn: "a column vector: C = K · P (Wikipedia, most textbooks)",
  conventionRow: "a row vector: C = P · K (Stinson)",
  filler: "Padding letter",
  fillerHint: "Completes the last block when the letters do not divide evenly.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  outputPlaceholder: "The result appears here.",
  lettersOnly:
    "Only the letters A–Z are enciphered: spaces, digits and punctuation are dropped, and the result is in capitals.",
  padded:
    "one: {count} padding letter was added to complete the last block.|other: {count} padding letters were added to complete the last block.",
  leftover:
    "one: The last {count} letter does not fill a block, so it was not decoded.|other: The last {count} letters do not fill a block, so they were not decoded.",
  paddingBack:
    "Padding added when the message was enciphered comes back at the end — delete it.",
  working: "How it was worked out",
  keyHeading: "Can this key be inverted?",
  detLine: "det K = {det} ≡ {detMod} (mod 26)",
  gcdLine: "gcd({detMod}, 26) = {gcd}",
  invertible:
    "Yes. {detMod} shares no factor with 26, so it has an inverse: det⁻¹ = {inverse}, because {detMod} × {inverse} = {product} ≡ 1 (mod 26).",
  notInvertibleEven:
    "No. {detMod} is even, so it shares the factor 2 with 26 and has no inverse mod 26.",
  notInvertible13:
    "No. {detMod} is a multiple of 13, a factor of 26, so it has no inverse mod 26.",
  notInvertibleZero:
    "No. The determinant is a multiple of 26, so it is 0 mod 26 and has no inverse.",
  whyInvertible:
    "A key works only if its determinant is odd and not a multiple of 13. Otherwise two different blocks encipher to the same letters, and the cipher text cannot be read back.",
  noKeyOutput:
    "This key has no inverse, so the tool will not use it: a message enciphered with it could not be read back.",
  suggestion:
    "Changing the entry in row {row}, column {column} from {from} to {to} would make the key invertible.",
  noSuggestion:
    "No change to a single entry can fix this key — every entry's cofactor shares a factor with 26. Start from a matrix with some odd entries.",
  stepDeterminant: "1. The determinant and its inverse",
  stepAdjugate: "2. The adjugate",
  adjugate2:
    "For a 2 × 2 matrix, swap the two diagonal entries and negate the other two.",
  adjugate3:
    "Each entry is a cofactor — the 2 × 2 determinant left when its row and column are removed, with signs alternating + − + — and the result is transposed.",
  stepInverse: "3. The inverse key",
  inverseHint: "Multiply the adjugate by det⁻¹ = {inverse} and reduce mod 26.",
  adjEquals: "adj(K) =",
  inverseEquals: "K⁻¹ =",
  identityEquals: "K · K⁻¹ ≡",
  congruent: "≡",
  modulo: "(mod 26)",
  adjugateLabel: "The adjugate of K",
  adjugateModLabel: "The adjugate of K, mod 26",
  inverseLabel: "The inverse key matrix",
  identityLabel: "K times its inverse, mod 26: the identity matrix",
  encodeBlocks: "Encryption, block by block",
  decodeBlocks: "4. Decryption, block by block",
  formulaColumnEncode: "C = K · P (mod 26)",
  formulaRowEncode: "C = P · K (mod 26)",
  formulaColumnDecode: "P = K⁻¹ · C (mod 26)",
  formulaRowDecode: "P = C · K⁻¹ (mod 26)",
  blockColumn: "Block",
  vectorColumn: "Numbers",
  productColumn: "Product",
  reducedColumn: "mod 26",
  lettersColumn: "Letters",
  blockLimit:
    "one: Showing the first block.|other: Showing the first {count} blocks.",
  noLetters: "Type some letters to see the blocks.",
} as const;

export type HillStrings = typeof HILL_CIPHER_STRINGS;

function KeyControls({
  size,
  onSize,
  source,
  onSource,
  entries,
  onEntries,
  keyword,
  onKeyword,
  matrix,
  convention,
  onConvention,
  filler,
  onFiller,
  strings,
}: {
  size: HillSize;
  onSize: (size: HillSize) => void;
  source: KeySource;
  onSource: (source: KeySource) => void;
  entries: readonly string[];
  onEntries: (next: string[]) => void;
  keyword: string;
  onKeyword: (keyword: string) => void;
  matrix: Matrix | null;
  convention: HillConvention;
  onConvention: (convention: HillConvention) => void;
  filler: Filler;
  onFiller: (filler: Filler) => void;
  strings: HillStrings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-4">
      <PillGroup
        label={strings.size}
        name="hill-size"
        onChange={(value) => onSize(value === "3" ? 3 : 2)}
        options={[
          { value: "2", label: strings.size2 },
          { value: "3", label: strings.size3 },
        ]}
        value={size === 3 ? "3" : "2"}
      />
      <PillGroup
        label={strings.keySource}
        name="hill-source"
        onChange={onSource}
        options={[
          { value: "numbers", label: strings.keyNumbers },
          { value: "keyword", label: strings.keyKeyword },
        ]}
        value={source}
      />
      {source === "numbers" ? (
        <MatrixInputs
          entries={entries}
          onChange={onEntries}
          size={size}
          strings={strings}
        />
      ) : (
        <div className="space-y-3">
          <TextField
            hint={t(strings.keywordHint, { count: fmt(size * size) })}
            id="hill-keyword"
            label={strings.keyword}
            mono
            onChange={onKeyword}
            value={keyword}
          />
          {matrix ? (
            <div className="space-y-1">
              <p className={LABEL}>{strings.keyFromKeyword}</p>
              <MatrixView
                format={fmt}
                label={strings.keyFromKeyword}
                matrix={matrix}
              />
            </div>
          ) : null}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
        <SelectField
          id="hill-convention"
          label={strings.convention}
          onChange={onConvention}
          options={[
            { value: "column", label: strings.conventionColumn },
            { value: "row", label: strings.conventionRow },
          ]}
          value={convention}
        />
        <SelectField
          hint={strings.fillerHint}
          id="hill-filler"
          label={strings.filler}
          onChange={onFiller}
          options={FILLERS.map((letter) => ({ value: letter, label: letter }))}
          value={filler}
        />
      </div>
    </div>
  );
}

function OutputNotes({
  result,
  mode,
  strings,
}: {
  result: HillResult | null;
  mode: CipherMode;
  strings: HillStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (!result || result.output === "") {
    return null;
  }
  return (
    <>
      {result.padded > 0 ? (
        <p className={HINT}>
          {tPlural(strings.padded, locale, result.padded, {
            count: fmt(result.padded),
          })}
        </p>
      ) : null}
      {result.leftover === "" ? null : (
        <p className={HINT}>
          {tPlural(strings.leftover, locale, result.leftover.length, {
            count: fmt(result.leftover.length),
          })}
        </p>
      )}
      {mode === "decode" ? <p className={HINT}>{strings.paddingBack}</p> : null}
    </>
  );
}

export default function HillCipherWidget() {
  const strings = useToolUi(HILL_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [size, setSize] = useState<HillSize>(2);
  const [source, setSource] = useState<KeySource>("numbers");
  const [entries, setEntries] =
    useState<Record<HillSize, readonly string[]>>(DEFAULT_ENTRIES);
  const [keywords, setKeywords] = useState<Record<HillSize, string>>({
    2: strings.keyword2,
    3: strings.keyword3,
  });
  const [convention, setConvention] = useState<HillConvention>("column");
  const [filler, setFiller] = useState<Filler>("X");

  const needed = size * size;
  const matrix =
    source === "numbers"
      ? matrixFromEntries(entries[size], size)
      : matrixFromKeyword(keywords[size], size);
  const invertible = matrix ? invertibility(matrix).detInverse !== null : false;
  let result: HillResult | null = null;
  if (matrix && invertible) {
    result =
      mode === "encode"
        ? hillEncrypt(input, matrix, convention, filler)
        : hillDecrypt(input, matrix, convention);
  }

  let keyNotice: string | null = null;
  if (!matrix) {
    keyNotice =
      source === "numbers"
        ? strings.badEntry
        : tPlural(strings.keywordShort, locale, needed, { count: fmt(needed) });
  } else if (!invertible) {
    keyNotice = strings.noKeyOutput;
  }

  return (
    <CipherWorkbench
      idPrefix="hill"
      input={input}
      keyControls={
        <KeyControls
          convention={convention}
          entries={entries[size]}
          filler={filler}
          keyword={keywords[size]}
          matrix={matrix}
          onConvention={setConvention}
          onEntries={(next) => setEntries({ ...entries, [size]: next })}
          onFiller={setFiller}
          onKeyword={(value) => setKeywords({ ...keywords, [size]: value })}
          onSize={setSize}
          onSource={setSource}
          size={size}
          source={source}
          strings={strings}
        />
      }
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder: strings.inputPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        outputPlaceholder: strings.outputPlaceholder,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={
        <>
          <p className={HINT}>{strings.lettersOnly}</p>
          {keyNotice ? <ErrorNotice>{keyNotice}</ErrorNotice> : null}
        </>
      }
      onInputChange={setInput}
      onModeChange={setMode}
      output={result?.output ?? ""}
      outputNotes={
        <OutputNotes mode={mode} result={result} strings={strings} />
      }
      working={
        matrix ? (
          <HillWorking
            convention={convention}
            matrix={matrix}
            mode={mode}
            result={result}
            strings={strings}
          />
        ) : null
      }
    />
  );
}
